import { clamp, easeOutCubic, lerp, randomBetween } from '../../core/math';
import type { Actor, Bounds, PainAction, Point, Surface } from '../../core/types';

interface Fragment {
  angle: number;
  phase: number;
  radius: number;
  speed: number;
}

const TAU = Math.PI * 2;
const fade = (time: number, end: number): number =>
  clamp(time / 0.25, 0, 1) * clamp((end - time) / 0.6, 0, 1);

/** Canvas-only props, sampled from simulation time: scrolling and pausing cannot advance them. */
export class PainEffects {
  private action: PainAction | undefined;
  private fragments: Fragment[] = [];

  start(action: PainAction): void {
    this.action = action;
    this.fragments = Array.from({ length: action === 'rain' ? 40 : 24 }, (_, i) => ({
      angle: (i / 24) * TAU + randomBetween(-0.15, 0.15),
      phase: Math.random(),
      radius: randomBetween(0.025, 0.065),
      speed: randomBetween(0.7, 1.2),
    }));
  }

  clear(): void {
    this.action = undefined;
    this.fragments = [];
  }

  draw(
    ctx: CanvasRenderingContext2D,
    hero: Actor,
    time: number,
    bounds: Bounds,
    target?: Surface,
  ): void {
    if (!this.action) return;
    ctx.save();
    ctx.lineCap = 'round';
    switch (this.action) {
      case 'shinratensei':
        this.repulsion(ctx, hero, time);
        break;
      case 'banshotenin':
        this.attraction(ctx, hero, time, target);
        break;
      case 'chibakutensei':
        this.moon(ctx, hero, time, bounds);
        break;
      case 'levitate':
        this.levitation(ctx, hero, time);
        break;
      case 'receivers':
        this.receiver(ctx, hero, time, bounds, target);
        break;
      case 'rinnegan':
        this.eyes(ctx, hero, time);
        break;
      case 'rain':
        this.rain(ctx, hero, time);
        break;
      case 'prepare':
        this.preparation(ctx, hero, time);
        break;
    }
    ctx.restore();
  }

  private repulsion(ctx: CanvasRenderingContext2D, hero: Actor, time: number): void {
    if (time < 0.65) return;
    const hand = { x: hero.x + hero.facing * hero.size * 0.34, y: hero.y - hero.size * 0.76 };
    for (let i = 0; i < 3; i++) {
      const progress = clamp((time - 0.65 - i * 0.12) / 1.2, 0, 1);
      if (!progress || progress === 1) continue;
      ctx.globalAlpha = (1 - progress) * 0.8;
      this.ring(
        ctx,
        hand,
        hero.size * (0.12 + easeOutCubic(progress) * 1.8),
        '#c2b1d6',
        3 - progress,
      );
      this.ring(ctx, hand, hero.size * (0.09 + easeOutCubic(progress) * 1.8), '#f8f2ff', 1.2);
    }
    const flight = clamp((time - 0.7) / 1.1, 0, 1);
    ctx.globalAlpha = (1 - flight) * 0.65;
    for (const part of this.fragments) {
      const radius = hero.size * (0.3 + flight * 1.7);
      const x = hand.x + Math.cos(part.angle) * radius;
      const y = hand.y + Math.sin(part.angle) * radius * 0.65;
      this.rock(ctx, x, y, hero.size * part.radius * 0.45, part.angle + flight, '#afa398');
    }
  }

  private attraction(
    ctx: CanvasRenderingContext2D,
    hero: Actor,
    time: number,
    target?: Surface,
  ): void {
    if (time < 0.5) return;
    ctx.globalAlpha = fade(time - 0.5, 2.6) * 0.8;
    const hand = { x: hero.x + hero.facing * hero.size * 0.32, y: hero.y - hero.size * 0.72 };
    const origin = target
      ? { x: target.x + target.width / 2, y: target.y + target.height / 2 }
      : { x: hero.x + hero.facing * hero.size * 1.4, y: hero.y - hero.size * 0.5 };
    this.glow(ctx, hand, hero.size * 0.15);
    for (let i = 0; i < 5; i++) {
      const progress = ((time - 0.5) * 0.8 + i / 5) % 1;
      const curve = Math.sin(progress * Math.PI) * hero.size * (i - 2) * 0.15;
      const x = lerp(origin.x, hand.x, progress);
      const y = lerp(origin.y, hand.y, progress) + curve;
      ctx.strokeStyle = i % 2 ? '#c7b5e1' : '#8e729f';
      ctx.lineWidth = 1.3;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.quadraticCurveTo(
        lerp(x, hand.x, 0.2),
        y + curve * 0.12,
        lerp(x, hand.x, 0.32),
        lerp(y, hand.y, 0.32),
      );
      ctx.stroke();
    }
  }

  private moon(ctx: CanvasRenderingContext2D, hero: Actor, time: number, bounds: Bounds): void {
    if (time < 0.65) return;
    const rise = easeOutCubic(clamp((time - 0.65) / 1, 0, 1));
    const orb = {
      x: clamp(hero.x, hero.size * 0.45, bounds.width - hero.size * 0.45),
      y: lerp(hero.y - hero.size * 0.7, Math.max(hero.size * 0.4, hero.y - hero.size * 1.6), rise),
    };
    const release = clamp((time - 4.8) / 1.7, 0, 1);
    ctx.globalAlpha = fade(time - 0.65, 5.85);
    const assembly = easeOutCubic(clamp((time - 1.8) / 1.5, 0, 1));
    this.glow(ctx, orb, hero.size * (0.2 + assembly * 0.22));
    this.ring(
      ctx,
      orb,
      hero.size * (0.1 + assembly * 0.26 + Math.sin(time * 2) * 0.008),
      '#a68ac2',
      1,
    );
    ctx.fillStyle = '#15111e';
    ctx.beginPath();
    ctx.arc(orb.x, orb.y, hero.size * 0.065 * rise, 0, TAU);
    ctx.fill();
    if (assembly > 0 && release < 0.3) {
      const radius = hero.size * 0.24 * assembly * (1 - release / 0.3);
      ctx.save();
      ctx.beginPath();
      ctx.arc(orb.x, orb.y, radius, 0, TAU);
      ctx.fillStyle = '#6d6375';
      ctx.fill();
      ctx.strokeStyle = '#443b52';
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.clip();
      for (const part of this.fragments) {
        const angle = part.angle + time * 0.16;
        this.rock(
          ctx,
          orb.x + Math.cos(angle) * radius * part.phase,
          orb.y + Math.sin(angle) * radius * part.phase,
          radius * 0.45,
          angle,
          part.phase > 0.5 ? '#a298a8' : '#82758d',
        );
      }
      ctx.restore();
    }
    for (const part of this.fragments) {
      const gather = easeOutCubic(clamp((time - 1.1 - part.phase * 0.8) / 1.9, 0, 1));
      if (!gather) continue;
      const angle = part.angle + time * 0.45 * part.speed;
      const radius = hero.size * (0.1 + part.phase * 0.22 + release * 0.8);
      const from = {
        x: clamp(hero.x + Math.cos(part.angle) * hero.size * 1.5, 12, bounds.width - 12),
        y: hero.y - 5,
      };
      const x = lerp(from.x, orb.x + Math.cos(angle) * radius, gather);
      const y =
        lerp(from.y, orb.y + Math.sin(angle) * radius * 0.85, gather) +
        release ** 2 * hero.size * 0.7;
      this.rock(
        ctx,
        x,
        y,
        hero.size * part.radius * (1 - release * 0.65),
        angle,
        part.phase > 0.5 ? '#736b78' : '#9b9299',
      );
    }
  }

  private levitation(ctx: CanvasRenderingContext2D, hero: Actor, time: number): void {
    ctx.globalAlpha = fade(time, 3.8) * 0.45;
    ctx.strokeStyle = '#ad94c0';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.ellipse(
      hero.x,
      hero.y + 7,
      hero.size * (0.23 + Math.sin(time * 3) * 0.02),
      hero.size * 0.055,
      0,
      0,
      TAU,
    );
    ctx.stroke();
    for (const part of this.fragments.slice(0, 8)) {
      const progress = (part.phase + time * 0.6) % 1;
      ctx.globalAlpha = Math.sin(progress * Math.PI) * 0.4;
      this.rock(
        ctx,
        hero.x + Math.cos(part.angle) * hero.size * 0.3,
        hero.y + progress * hero.size * 0.22,
        1.5,
        part.angle,
        '#b6a5bf',
      );
    }
  }

  private receiver(
    ctx: CanvasRenderingContext2D,
    hero: Actor,
    time: number,
    bounds: Bounds,
    target?: Surface,
  ): void {
    if (time < 0.65) return;
    const end = {
      x: target
        ? target.x + target.width * 0.65
        : clamp(hero.x + hero.facing * hero.size * 0.8, 20, bounds.width - 20),
      y: target?.y ?? bounds.height - 14,
    };
    const start = { x: hero.x + hero.facing * hero.size * 0.22, y: hero.y - hero.size * 0.4 };
    const progress = clamp((time - 0.65) / 0.5, 0, 1);
    const x = lerp(start.x, end.x, progress);
    const y = lerp(start.y, end.y, progress) - Math.sin(progress * Math.PI) * hero.size * 0.3;
    ctx.globalAlpha = fade(time - 0.65, 2.95);
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(lerp(hero.facing * 0.9, -0.15, progress));
    const length = hero.size * 0.38;
    ctx.fillStyle = '#15131d';
    ctx.strokeStyle = '#63536c';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, 3);
    ctx.lineTo(-2.3, -length + 3);
    ctx.lineTo(2.3, -length);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    if (progress < 1) return;
    for (let i = 0; i < 5; i++) {
      const angle = (i / 5) * TAU;
      const dx = Math.cos(angle) * hero.size * 0.12;
      const dy = Math.sin(angle) * hero.size * 0.035;
      ctx.strokeStyle = '#756b83';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(end.x, end.y + 1);
      ctx.lineTo(end.x + dx * 0.5, end.y + dy - 2);
      ctx.lineTo(end.x + dx, end.y + dy * 0.7 + 2);
      ctx.stroke();
    }
    if (time < 1.7) {
      ctx.globalAlpha *= (1.7 - time) / 0.55;
      this.ring(ctx, end, hero.size * (time - 1.15) * 0.5, '#c0a4d2', 1.5);
    }
  }

  private eyes(ctx: CanvasRenderingContext2D, hero: Actor, time: number): void {
    const eye = { x: hero.x + hero.facing * hero.size * 0.07, y: hero.y - hero.size * 0.885 };
    ctx.globalAlpha = fade(time, 3.2) * (0.4 + Math.sin(time * 4) * 0.15);
    this.glow(ctx, eye, hero.size * 0.05);
    for (let i = 0; i < 3; i++) this.ring(ctx, eye, hero.size * (0.01 + i * 0.01), '#a893c7', 0.7);
  }

  private rain(ctx: CanvasRenderingContext2D, hero: Actor, time: number): void {
    const alpha = fade(time, 4.8);
    for (const part of this.fragments) {
      const fall = (part.phase + time * 1.8 * part.speed) % 1;
      const x = hero.x + Math.cos(part.angle) * hero.size * 0.65;
      const y = hero.y - hero.size * 1.2 + fall * hero.size * 1.2;
      ctx.globalAlpha = alpha * 0.55;
      ctx.strokeStyle = '#7693ae';
      ctx.lineWidth = 0.9;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x - 2, y + hero.size * 0.06);
      ctx.stroke();
      if (fall > 0.82) {
        const splash = (fall - 0.82) / 0.18;
        ctx.globalAlpha = alpha * (1 - splash) * 0.6;
        ctx.beginPath();
        ctx.ellipse(x, hero.y + 2, 1 + splash * 5, 0.5 + splash * 1.5, 0, 0, TAU);
        ctx.stroke();
      }
    }
  }

  private preparation(ctx: CanvasRenderingContext2D, hero: Actor, time: number): void {
    ctx.globalAlpha = fade(time, 4) * 0.7;
    const hand = { x: hero.x + hero.facing * hero.size * 0.015, y: hero.y - hero.size * 0.66 };
    this.glow(ctx, hand, hero.size * (0.08 + Math.sin(time * 5) * 0.015));
    const rise = Math.sin(clamp(time / 4, 0, 1) * Math.PI);
    for (const part of this.fragments.slice(0, 8)) {
      const x = hero.x + Math.cos(part.angle) * hero.size * 0.45;
      const y = hero.y - rise * hero.size * (0.08 + part.phase * 0.2);
      this.rock(ctx, x, y, hero.size * part.radius * 0.6, part.angle + time * 0.7, '#8d7d91');
    }
  }

  private ring(
    ctx: CanvasRenderingContext2D,
    point: Point,
    radius: number,
    color: string,
    width: number,
  ): void {
    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    ctx.beginPath();
    ctx.arc(point.x, point.y, Math.max(0, radius), 0, TAU);
    ctx.stroke();
  }

  private glow(ctx: CanvasRenderingContext2D, point: Point, radius: number): void {
    const gradient = ctx.createRadialGradient(point.x, point.y, 0, point.x, point.y, radius);
    gradient.addColorStop(0, '#e7d7ffbb');
    gradient.addColorStop(0.35, '#b08adc55');
    gradient.addColorStop(1, '#a78aca00');
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(point.x, point.y, radius, 0, TAU);
    ctx.fill();
  }

  private rock(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    size: number,
    angle: number,
    color: string,
  ): void {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle);
    ctx.fillStyle = color;
    ctx.strokeStyle = '#443b52';
    ctx.lineWidth = 0.7;
    ctx.beginPath();
    ctx.moveTo(-size, -size * 0.4);
    ctx.lineTo(-size * 0.25, -size);
    ctx.lineTo(size * 0.8, -size * 0.6);
    ctx.lineTo(size, size * 0.45);
    ctx.lineTo(-size * 0.5, size * 0.8);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = '#d4c6de66';
    ctx.beginPath();
    ctx.moveTo(-size, -size * 0.4);
    ctx.lineTo(-size * 0.25, -size);
    ctx.lineTo(size * 0.8, -size * 0.6);
    ctx.lineTo(0, 0);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }
}
